// Rental Wizard Types - Based on @kgc/rental-core interfaces

export type WizardPartnerStatus = 'active' | 'inactive' | 'blocked' | 'trusted' | 'warning';

export interface Partner {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  type: 'INDIVIDUAL' | 'COMPANY';
  status?: WizardPartnerStatus;
  taxNumber?: string;
  address?: string;
  city?: string;
  isVip: boolean;
  /** FE-E: hűségpont-egyenleg (a loyalty-csúszka max-értéke a SummaryStep-en). */
  loyaltyPoints?: number;
  creditLimit: number;
  balance: number;
  warningNote?: string;
  costBearerId?: string;
  costBearerName?: string;
  maskedIdCardNumber?: string;
  /** Story 70-1E-a: idCardExpiry (ISO date) — past value triggers override UI. */
  idCardExpiry?: string | undefined;
}

export interface Accessory {
  id: string;
  name: string;
  description?: string;
  isRequired: boolean;
  value?: number;
}

export interface Equipment {
  id: string;
  name: string;
  equipmentCode?: string;
  serialNumber: string;
  category: EquipmentCategory;
  brand?: string;
  model?: string;
  status: EquipmentStatus;
  condition: EquipmentCondition;
  dailyRate: number;
  weeklyRate: number;
  monthlyRate: number;
  hourlyRate?: number;
  depositAmount: number;
  imageUrl?: string;
  accessories?: Accessory[];
}

export enum EquipmentStatus {
  AVAILABLE = 'AVAILABLE',
  RENTED = 'RENTED',
  IN_SERVICE = 'IN_SERVICE',
  RESERVED = 'RESERVED',
  MAINTENANCE_REQUIRED = 'MAINTENANCE_REQUIRED',
  DAMAGED = 'DAMAGED',
  DECOMMISSIONED = 'DECOMMISSIONED',
  LOST = 'LOST',
  SOLD = 'SOLD',
  SCRAPPED = 'SCRAPPED',
}

export enum EquipmentCategory {
  POWER_TOOL = 'POWER_TOOL',
  GARDEN = 'GARDEN',
  CONSTRUCTION = 'CONSTRUCTION',
  CLEANING = 'CLEANING',
  MACHINERY = 'MACHINERY',
  HAND_TOOL = 'HAND_TOOL',
  MEASUREMENT = 'MEASUREMENT',
  SAFETY = 'SAFETY',
}

export enum EquipmentCondition {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  POOR = 'POOR',
  DAMAGED = 'DAMAGED',
  DAMAGED_USABLE = 'DAMAGED_USABLE',
  DAMAGED_SERVICE = 'DAMAGED_SERVICE',
  INCOMPLETE = 'INCOMPLETE',
}

export enum PricingTier {
  HOURLY = 'HOURLY',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum PaymentMethod {
  CASH = 'CASH',
  CARD = 'CARD',
  TRANSFER = 'TRANSFER',
}

export interface CartItem {
  id: string;
  equipment: Equipment;
  quantity: number;
  addedAt: Date;
}

export interface CartPricingItem {
  cartItemId: string;
  equipmentId: string;
  equipmentName: string;
  tier: PricingTier;
  dailyRate: number;
  hourlyRate?: number;
  durationDays: number;
  durationHours?: number;
  itemSubtotal: number;
  itemVat: number;
  itemTotal: number;
  deposit: number;
}

export type BreakdownDayType = 'WORKDAY' | 'SUNDAY' | 'HOLIDAY';

export interface BreakdownDay {
  date: string;
  dayOfWeek: number;
  type: BreakdownDayType;
  value: number;
  holidayName?: string | undefined;
}

export interface CartPricing {
  items: CartPricingItem[];
  durationDays: number;
  totalHours?: number;
  useHourlyBilling?: boolean;
  subtotal: number;
  discountTotal: number;
  vatAmount: number;
  grandTotal: number;
  depositTotal: number;
  breakdownDays: BreakdownDay[];
  weeklyDiscountDays: number;
  isMonthlyMode: boolean;
  billableDays: number;
  /** BE-story #2: berelheto tartozek-berleti dij netto osszege (rentAccNet).
   *  Beleszamit a discountCalculation elotti net-be (VAT-alap). */
  accessoryRentNet: number;
  /** BE-story #2: bizomanyos (CONSIGNMENT) tartozekok netto erteke.
   *  Kulon track-elodik - NEM berleti dij, nem kedvezmenykez. */
  consignmentNet: number;
  /** BE-story #2: osszes kivalasztott tartozek-darabszam (accCount). */
  accessoryCount: number;
}

export interface RentalPricing {
  tier: PricingTier;
  durationDays: number;
  dailyRate: number;
  grossAmount: number;
  discountAmount: number;
  netAmount: number;
  vatRate: number;
  vatAmount: number;
  totalAmount: number;
  depositAmount: number;
}

export interface WizardState {
  activeStep: 1 | 2 | 3 | 4;
  partner: Partner | null;
  costBearerId: string | null;
  equipment: Equipment | null;
  cart: CartItem[];
  selectedAccessories: Map<string, string[]>;
  startDate: Date | null;
  endDate: Date | null;
  startTime: string | null;
  endTime: string | null;
  useHourlyBilling: boolean;
  pricing: CartPricing | null;
  paymentMethod: PaymentMethod | null;
  notes: string;
}

export const CATEGORY_LABEL_KEYS: Record<EquipmentCategory, string> = {
  [EquipmentCategory.POWER_TOOL]: 'rental.category.electricMachines',
  [EquipmentCategory.GARDEN]: 'rental.category.outdoorMachines',
  [EquipmentCategory.CONSTRUCTION]: 'rental.category.construction',
  [EquipmentCategory.CLEANING]: 'rental.category.cleaning',
  [EquipmentCategory.MACHINERY]: 'rental.category.machinery',
  [EquipmentCategory.HAND_TOOL]: 'rental.category.handTool',
  [EquipmentCategory.MEASUREMENT]: 'rental.category.measurement',
  [EquipmentCategory.SAFETY]: 'rental.category.safety',
};

export const STATUS_LABEL_KEYS: Record<EquipmentStatus, string> = {
  [EquipmentStatus.AVAILABLE]: 'rental.equipmentStatus.available',
  [EquipmentStatus.RENTED]: 'rental.equipmentStatus.rented',
  [EquipmentStatus.IN_SERVICE]: 'rental.equipmentStatus.inService',
  [EquipmentStatus.RESERVED]: 'rental.equipmentStatus.reserved',
  [EquipmentStatus.MAINTENANCE_REQUIRED]: 'rental.equipmentStatus.maintenanceRequired',
  [EquipmentStatus.DAMAGED]: 'rental.equipmentStatus.damaged',
  [EquipmentStatus.DECOMMISSIONED]: 'rental.equipmentStatus.decommissioned',
  [EquipmentStatus.LOST]: 'rental.equipmentStatus.lost',
  [EquipmentStatus.SOLD]: 'rental.equipmentStatus.sold',
  [EquipmentStatus.SCRAPPED]: 'rental.equipmentStatus.scrapped',
};

/**
 * Equipment status color mapping for Badge components.
 * Colors per ERP-TESZT 9: RED=rented, GREY=service, GREEN=available, ORANGE=reserved.
 * Uses dark: variants for WCAG AA contrast compliance.
 */
export const EQUIPMENT_STATUS_COLORS: Record<EquipmentStatus, string> = {
  [EquipmentStatus.AVAILABLE]:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  [EquipmentStatus.RENTED]: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  [EquipmentStatus.IN_SERVICE]: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  [EquipmentStatus.RESERVED]:
    'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  [EquipmentStatus.MAINTENANCE_REQUIRED]:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  [EquipmentStatus.DAMAGED]: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  [EquipmentStatus.DECOMMISSIONED]:
    'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
  [EquipmentStatus.LOST]:
    'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  [EquipmentStatus.SOLD]: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  [EquipmentStatus.SCRAPPED]:
    'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
};

export const CONDITION_LABEL_KEYS: Record<EquipmentCondition, string> = {
  [EquipmentCondition.EXCELLENT]: 'rental.condition.excellent',
  [EquipmentCondition.GOOD]: 'rental.condition.good',
  [EquipmentCondition.FAIR]: 'rental.condition.fair',
  [EquipmentCondition.POOR]: 'rental.condition.poor',
  [EquipmentCondition.DAMAGED]: 'rental.condition.damaged',
  [EquipmentCondition.DAMAGED_USABLE]: 'rental.condition.damagedUsable',
  [EquipmentCondition.DAMAGED_SERVICE]: 'rental.condition.damagedService',
  [EquipmentCondition.INCOMPLETE]: 'rental.condition.incomplete',
};

export interface RentalItem {
  id: string;
  equipmentId: string;
  equipment: Equipment;
  quantity: number;
  dailyRate: number;
  appliedRate: number;
  itemTotal: number;
  condition?: EquipmentCondition;
}

// Rental (Active rental contract)
export enum RentalStatus {
  ACTIVE = 'ACTIVE',
  OVERDUE = 'OVERDUE',
  PARTIALLY_RETURNED = 'PARTIALLY_RETURNED',
  RETURNED = 'RETURNED',
  CANCELLED = 'CANCELLED',
}

export interface Rental {
  id: string;
  contractNumber: string;
  partner: Partner;
  equipment?: Equipment;
  items?: RentalItem[];
  startDate: string;
  endDate: string;
  actualReturnDate?: string;
  status: RentalStatus;
  pricing: RentalPricing | CartPricing;
  depositPaid: number;
  rentalPaid: number;
  lateFee: number;
  damageCharge: number;
  returnNotes?: string;
  returnCondition?: EquipmentCondition;
  createdAt: string;
  createdBy: string;
}

export const RENTAL_STATUS_LABEL_KEYS: Record<RentalStatus, string> = {
  [RentalStatus.ACTIVE]: 'rental.status.active',
  [RentalStatus.OVERDUE]: 'rental.status.expired',
  [RentalStatus.PARTIALLY_RETURNED]: 'rental.status.returned',
  [RentalStatus.RETURNED]: 'rental.status.returned',
  [RentalStatus.CANCELLED]: 'rental.status.cancelled',
};

// ============================================
// Cross-sell types (Story Q-XSL-1)
// ============================================

/**
 * A product that can be sold alongside a rental (cross-sell).
 * Uses the same shape as POS Product for compatibility.
 */
export interface CrossSellProduct {
  id: string;
  sku: string;
  name: string;
  barcode: string | null;
  category: string;
  price: number;
  vatRate: number;
  stock: number;
  unit: string;
}

/**
 * Cross-sell cart item — product added during rental checkout.
 */
export interface CrossSellCartItem {
  id: string;
  product: CrossSellProduct;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}
